<template>
  <div class="widget-body">
    <c-loader v-if="isLoading" />
    <v-alert show v-if="isNoData" variant="light" align="center">{{ $t('white-label.globalMessages.nodata') }}</v-alert>
    <div
      v-if="!hasError && !(balanceItems.length === 0 || profitLossItems.length === 0 || cashFlowItems.length === 0)"
      v-show="!isLoading"
    >
      <b-table
        dark
        small
        striped
        hover
        bordered
        selectable
        select-mode="single"
        caption-top
        @head-clicked="onHeaderClicked"
        @keydown.tab.native="suppressTab"
        v-for="(tableName, i) in tableNames"
        :fixed="true"
        :class="{ 'mb-4': i < 2 }"
        :key="tableName"
        :fields="getTableFields(tableName)"
        :items="getItems(tableName)"
        :aria-label="$t(`${i18nNamespace}.tables.${tableName}.title`)"
      >
        <template #table-caption>{{ $t(`${i18nNamespace}.tables.${tableName}.title`) }}</template>

        <template #head(3y)>
          {{ $f(tableDates[tableName]['3y'], 'date.localed') }}
        </template>
        <template #head(2y)>
          {{ $f(tableDates[tableName]['2y'], 'date.localed') }}
        </template>
        <template #head(1y)>
          {{ $f(tableDates[tableName]['1y'], 'date.localed') }}
        </template>
        <template #head(year_end)>
          {{ $f(tableDates[tableName]['year_end'], 'date.localed') }}
        </template>

        <template v-slot:cell(title)="data">
          {{ $t(`${i18nNamespace}.tables.${tableName}.rowTitles.${data.item.title}`) }}
        </template>
        <template v-slot:cell(3y)="data">
          <span v-if="data.item.noFormatting">{{ data.value }}</span>
          <v-fidlet v-else :data="data.value || ''" type="float.price" />
        </template>
        <template v-slot:cell(2y)="data">
          <span v-if="data.item.noFormatting">{{ data.value }}</span>
          <v-fidlet v-else :data="data.value || ''" type="float.price" />
        </template>
        <template v-slot:cell(1y)="data">
          <span v-if="data.item.noFormatting">{{ data.value }}</span>
          <v-fidlet v-else :data="data.value || ''" type="float.price" />
        </template>
        <template v-slot:cell(year_end)="data">
          <span v-if="data.item.noFormatting">{{ data.value }}</span>
          <v-fidlet v-else :data="data.value || ''" type="float.price" />
        </template>

        <template #custom-foot>
          <b-tr>
            <b-td>
              {{
                $t(`${i18nNamespace}.tables.${tableName}.rowTitles.${tableFooter[tableName].title}`) + ' ' + currency
              }}
            </b-td>
          </b-tr>
        </template>
      </b-table>
    </div>

    <div
      class="text-center"
      v-if="!hasError && balanceItems.length === 0 && profitLossItems.length === 0 && cashFlowItems.length === 0"
    >
      {{ $t(`${i18nNamespace}.noData`) }}
    </div>
    <div class="text-center" tabindex="0" v-if="hasError && !isNoData">{{ $t(`${i18nNamespace}.error`) }}</div>
  </div>
</template>
<script>
import CLoader from '@custom/CLoader';

import { API } from '@/state/api';

export default {
  name: 'w-financial-statements-widget',
  components: {
    CLoader
  },
  props: {
    ric: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isLoading: false,
      isNoData: false,
      hasError: false,
      financialStatementsData: null,
      balanceType: null,
      currency: null,
      tableNames: ['balance', 'profitLoss', 'cashFlow'],
      tables: {
        BNK: {
          balance: {
            columnDates: [
              'rkdp.FINYEAREND_DATE_LST3Y',
              'rkdp.FINYEAREND_DATE_LST2Y',
              'rkdp.FINYEAREND_DATE_LST1Y',
              'rkdp.FINYEAREND_DATE'
            ],
            accountingMethod: [
              'rkdp.ACCOUNTING_STD_LST3Y',
              'rkdp.ACCOUNTING_STD_LST2Y',
              'rkdp.ACCOUNTING_STD_LST1Y',
              'rkdp.ACCOUNTING_STD'
            ],
            assets: ['-', '-', '-', '-'],
            bankCash: ['rkdp.BANK_CASH_LST3Y', 'rkdp.BANK_CASH_LST2Y', 'rkdp.BANK_CASH_LST1Y', 'rkdp.BANK_CASH'],
            otherAssetsBank: [
              'rkdp.BANK_OTHER_EARN_ASSETS_LST3Y',
              'rkdp.BANK_OTHER_EARN_ASSETS_LST2Y',
              'rkdp.BANK_OTHER_EARN_ASSETS_LST1Y',
              'rkdp.BANK_OTHER_EARN_ASSETS'
            ],
            depositsFinancialInstitutions: [
              'rkdp.BANK_DEPOSITS_ASSETS_LST3Y',
              'rkdp.BANK_DEPOSITS_ASSETS_LST2Y',
              'rkdp.BANK_DEPOSITS_ASSETS_LST1Y',
              'rkdp.BANK_DEPOSITS_ASSETS'
            ],
            bonds: ['rkdp.BANK_BONDS_LST3Y', 'rkdp.BANK_BONDS_LST2Y', 'rkdp.BANK_BONDS_LST1Y', 'rkdp.BANK_BONDS'],
            otherFinancialAssets: [
              'rkdp.BANK_OTHER_SHORT_ASSETS_LST3Y',
              'rkdp.BANK_OTHER_SHORT_ASSETS_LST2Y',
              'rkdp.BANK_OTHER_SHORT_ASSETS_LST1Y',
              'rkdp.BANK_OTHER_SHORT_ASSETS'
            ],
            receivablesFromCreditTransactions: [
              'rkdp.BANK_LOANS_LST3Y',
              'rkdp.BANK_LOANS_LST2Y',
              'rkdp.BANK_LOANS_LST1Y',
              'rkdp.BANK_LOANS'
            ],
            otherAssetsBank2: [
              'rkdp.BANK_OTHER_ASSETS_LST3Y',
              'rkdp.BANK_OTHER_ASSETS_LST2Y',
              'rkdp.BANK_OTHER_ASSETS_LST1Y',
              'rkdp.BANK_OTHER_ASSETS'
            ],
            otherAssets: [
              'rkdp.OTHER_ASSETS_LST3Y',
              'rkdp.OTHER_ASSETS_LST2Y',
              'rkdp.OTHER_ASSETS_LST1Y',
              'rkdp.OTHER_ASSETS'
            ],
            property: [
              'rkdp.BANK_PROPERTY_LST3Y',
              'rkdp.BANK_PROPERTY_LST2Y',
              'rkdp.BANK_PROPERTY_LST1Y',
              'rkdp.BANK_PROPERTY'
            ],
            investments: [
              'rkdp.BANK_LONG_INVESTMENTS_LST3Y',
              'rkdp.BANK_LONG_INVESTMENTS_LST2Y',
              'rkdp.BANK_LONG_INVESTMENTS_LST1Y',
              'rkdp.BANK_LONG_INVESTMENTS'
            ],
            accrualsAndDeferrals: [
              'rkdp.ACCRUALS_LST3Y',
              'rkdp.ACCRUALS_LST2Y',
              'rkdp.ACCRUALS_LST1Y',
              'rkdp.ACCRUALS'
            ],
            balanceSheetTotal: [
              'rkdp.TOTAL_ASSETS_LST3Y',
              'rkdp.TOTAL_ASSETS_LST2Y',
              'rkdp.TOTAL_ASSETS_LST1Y',
              'rkdp.TOTAL_ASSETS'
            ],
            liabilities: ['-', '-', '-', '-'],
            equityCapital: ['rkdp.EQUITY_LST3Y', 'rkdp.EQUITY_LST2Y', 'rkdp.EQUITY_LST1Y', 'rkdp.EQUITY'],
            shareCapital: [
              'rkdp.NOMINAL_CAPITAL_LST3Y',
              'rkdp.NOMINAL_CAPITAL_LST2Y',
              'rkdp.NOMINAL_CAPITAL_LST1Y',
              'rkdp.NOMINAL_CAPITAL'
            ],
            commitments: [
              'rkdp.LIABILITIES_LST3Y',
              'rkdp.LIABILITIES_LST2Y',
              'rkdp.LIABILITIES_LST1Y',
              'rkdp.LIABILITIES'
            ],
            reserves: ['rkdp.PROVISION_LST3Y', 'rkdp.PROVISION_LST2Y', 'rkdp.PROVISION_LST1Y', 'rkdp.PROVISION'],
            deposits: [
              'rkdp.BANK_DEPOSIT_LIABILITIES_LST3Y',
              'rkdp.BANK_DEPOSIT_LIABILITIES_LST2Y',
              'rkdp.BANK_DEPOSIT_LIABILITIES_LST1Y',
              'rkdp.BANK_DEPOSIT_LIABILITIES'
            ],
            otherShortTermLiabilities: [
              'rkdp.BANK_SHORT_LIABILITIES_LST3Y',
              'rkdp.BANK_SHORT_LIABILITIES_LST2Y',
              'rkdp.BANK_SHORT_LIABILITIES_LST1Y',
              'rkdp.BANK_SHORT_LIABILITIES'
            ],
            longTermLiabilities: [
              'rkdp.LONG_TERM_DEBT_LST3Y',
              'rkdp.LONG_TERM_DEBT_LST2Y',
              'rkdp.LONG_TERM_DEBT_LST1Y',
              'rkdp.LONG_TERM_DEBT'
            ],
            otherLiabilities: [
              'rkdp.BANK_OTHER_LIABILITIES_LST3Y',
              'rkdp.BANK_OTHER_LIABILITIES_LST2Y',
              'rkdp.BANK_OTHER_LIABILITIES_LST1Y',
              'rkdp.BANK_OTHER_LIABILITIES'
            ],
            accruedExpenses: [
              'rkdp.DEFERRED_INCOME_LST3Y',
              'rkdp.DEFERRED_INCOME_LST2Y',
              'rkdp.DEFERRED_INCOME_LST1Y',
              'rkdp.DEFERRED_INCOME'
            ],
            minorityInterest: [
              'rkdp.MINORITY_INTEREST_LST3Y',
              'rkdp.MINORITY_INTEREST_LST2Y',
              'rkdp.MINORITY_INTEREST_LST1Y',
              'rkdp.MINORITY_INTEREST'
            ],
            balanceSheetTotal2: [
              'rkdp.TOTAL_ASSETS_LST3Y',
              'rkdp.TOTAL_ASSETS_LST2Y',
              'rkdp.TOTAL_ASSETS_LST1Y',
              'rkdp.TOTAL_ASSETS'
            ],
            allValues: ['-', '-', '-', '-']
          },
          profitLoss: {
            columnDates: [
              'rkdp.FINYEAREND_DATE_LST3Y',
              'rkdp.FINYEAREND_DATE_LST2Y',
              'rkdp.FINYEAREND_DATE_LST1Y',
              'rkdp.FINYEAREND_DATE'
            ],
            interestSurplus: [
              'rkdp.BANK_INTEREST_SURPLUS_LST3Y',
              'rkdp.BANK_INTEREST_SURPLUS_LST2Y',
              'rkdp.BANK_INTEREST_SURPLUS_LST1Y',
              'rkdp.BANK_INTEREST_SURPLUS'
            ],
            independentRevenues: [
              'rkdp.BANK_EARNINGS_LST3Y',
              'rkdp.BANK_EARNINGS_LST2Y',
              'rkdp.BANK_EARNINGS_LST1Y',
              'rkdp.BANK_EARNINGS'
            ],
            independentExpenses: [
              'rkdp.BANK_EXPENSES_LST3Y',
              'rkdp.BANK_EXPENSES_LST2Y',
              'rkdp.BANK_EXPENSES_LST1Y',
              'rkdp.BANK_EXPENSES'
            ],
            ebit: ['rkdp.EBIT_LST3Y', 'rkdp.EBIT_LST2Y', 'rkdp.EBIT_LST1Y', 'rkdp.EBIT'],
            taxes: ['rkdp.TAXES_LST3Y', 'rkdp.TAXES_LST2Y', 'rkdp.TAXES_LST1Y', 'rkdp.TAXES'],
            annualNetProfit: [
              'rkdp.ANNUAL_NET_PROFIT_LST3Y',
              'rkdp.ANNUAL_NET_PROFIT_LST2Y',
              'rkdp.ANNUAL_NET_PROFIT_LST1Y',
              'rkdp.ANNUAL_NET_PROFIT'
            ],
            allValues: ['-', '-', '-', '-']
          },
          cashFlow: {
            columnDates: [
              'rkdp.FINYEAREND_DATE_LST3Y',
              'rkdp.FINYEAREND_DATE_LST2Y',
              'rkdp.FINYEAREND_DATE_LST1Y',
              'rkdp.FINYEAREND_DATE'
            ],
            cashFlowOperating: [
              'rkdp.CASH_FLOW_OPERATING_LST3Y',
              'rkdp.CASH_FLOW_OPERATING_LST2Y',
              'rkdp.CASH_FLOW_OPERATING_LST1Y',
              'rkdp.CASH_FLOW_OPERATING'
            ],
            cashFlowFinancing: [
              'rkdp.CASH_FLOW_FINANCING_LST3Y',
              'rkdp.CASH_FLOW_FINANCING_LST2Y',
              'rkdp.CASH_FLOW_FINANCING_LST1Y',
              'rkdp.CASH_FLOW_FINANCING'
            ],
            cashFlowInvesting: [
              'rkdp.CASH_FLOW_INVESTING_LST3Y',
              'rkdp.CASH_FLOW_INVESTING_LST2Y',
              'rkdp.CASH_FLOW_INVESTING_LST1Y',
              'rkdp.CASH_FLOW_INVESTING'
            ],
            allValues: ['-', '-', '-', '-']
          }
        },
        IND: {
          balance: {
            columnDates: [
              'rkdp.FINYEAREND_DATE_LST3Y',
              'rkdp.FINYEAREND_DATE_LST2Y',
              'rkdp.FINYEAREND_DATE_LST1Y',
              'rkdp.FINYEAREND_DATE'
            ],
            accountingMethod: [
              'rkdp.ACCOUNTING_STD_LST3Y',
              'rkdp.ACCOUNTING_STD_LST2Y',
              'rkdp.ACCOUNTING_STD_LST1Y',
              'rkdp.ACCOUNTING_STD'
            ],
            assets: ['-', '-', '-', '-'],
            workingAssets: [
              'rkdp.WORKING_ASSETS_LST3Y',
              'rkdp.WORKING_ASSETS_LST2Y',
              'rkdp.WORKING_ASSETS_LST1Y',
              'rkdp.WORKING_ASSETS'
            ],
            cashAssets: [
              'rkdp.CASH_AND_EQUIV_LST3Y',
              'rkdp.CASH_AND_EQUIV_LST2Y',
              'rkdp.CASH_AND_EQUIV_LST1Y',
              'rkdp.CASH_AND_EQUIV'
            ],
            receivables: [
              'rkdp.RECEIVABLES_LST3Y',
              'rkdp.RECEIVABLES_LST2Y',
              'rkdp.RECEIVABLES_LST1Y',
              'rkdp.RECEIVABLES'
            ],
            otherCurrentAssets: [
              'rkdp.OTHER_CURRENT_ASSETS_LST3Y',
              'rkdp.OTHER_CURRENT_ASSETS_LST2Y',
              'rkdp.OTHER_CURRENT_ASSETS_LST1Y',
              'rkdp.OTHER_CURRENT_ASSETS'
            ],
            accruals: [
              'rkdp.ACCRUALS_CURRENT_LST3Y',
              'rkdp.ACCRUALS_CURRENT_LST2Y',
              'rkdp.ACCRUALS_CURRENT_LST1Y',
              'rkdp.ACCRUALS_CURRENT'
            ],
            fixedAssets: [
              'rkdp.FIXED_ASSETS_LST3Y',
              'rkdp.FIXED_ASSETS_LST2Y',
              'rkdp.FIXED_ASSETS_LST1Y',
              'rkdp.FIXED_ASSETS'
            ],
            fixedAccruals: ['rkdp.ACCRUALS_LST3Y', 'rkdp.ACCRUALS_LST2Y', 'rkdp.ACCRUALS_LST1Y', 'rkdp.ACCRUALS'],
            balanceSheetTotal: [
              'rkdp.TOTAL_ASSETS_LST3Y',
              'rkdp.TOTAL_ASSETS_LST2Y',
              'rkdp.TOTAL_ASSETS_LST1Y',
              'rkdp.TOTAL_ASSETS'
            ],
            liabilities: [
              'rkdp.FINYEAREND_DATE_LST3Y',
              'rkdp.FINYEAREND_DATE_LST2Y',
              'rkdp.FINYEAREND_DATE_LST1Y',
              'rkdp.FINYEAREND_DATE'
            ],
            equityCapital: ['rkdp.EQUITY_LST3Y', 'rkdp.EQUITY_LST2Y', 'rkdp.EQUITY_LST1Y', 'rkdp.EQUITY'],
            shareCapital: [
              'rkdp.NOMINAL_CAPITAL_LST3Y',
              'rkdp.NOMINAL_CAPITAL_LST2Y',
              'rkdp.NOMINAL_CAPITAL_LST1Y',
              'rkdp.NOMINAL_CAPITAL'
            ],
            liabilitiesIND: [
              'rkdp.LIABILITIES_LST3Y',
              'rkdp.LIABILITIES_LST2Y',
              'rkdp.LIABILITIES_LST1Y',
              'rkdp.LIABILITIES'
            ],
            longTermLiabilities: [
              'rkdp.LONG_TERM_DEBT_LST3Y',
              'rkdp.LONG_TERM_DEBT_LST2Y',
              'rkdp.LONG_TERM_DEBT_LST1Y',
              'rkdp.LONG_TERM_DEBT'
            ],
            shortTermLiabilities: [
              'rkdp.LIABILITIES_CURRENT_LST3Y',
              'rkdp.LIABILITIES_CURRENT_LST2Y',
              'rkdp.LIABILITIES_CURRENT_LST1Y',
              'rkdp.LIABILITIES_CURRENT'
            ],
            reserves: ['rkdp.PROVISION_LST3Y', 'rkdp.PROVISION_LST2Y', 'rkdp.PROVISION_LST1Y', 'rkdp.PROVISION'],
            minorityInterest: [
              'rkdp.MINORITY_INTEREST_LST3Y',
              'rkdp.MINORITY_INTEREST_LST2Y',
              'rkdp.MINORITY_INTEREST_LST1Y',
              'rkdp.MINORITY_INTEREST'
            ],
            accruedExpenses: [
              'rkdp.DEFERRED_INCOME_LST3Y',
              'rkdp.DEFERRED_INCOME_LST2Y',
              'rkdp.DEFERRED_INCOME_LST1Y',
              'rkdp.DEFERRED_INCOME'
            ],
            balanceSheetTotal2: [
              'rkdp.TOTAL_ASSETS_LST3Y',
              'rkdp.TOTAL_ASSETS_LST2Y',
              'rkdp.TOTAL_ASSETS_LST1Y',
              'rkdp.TOTAL_ASSETS'
            ],
            allValues: ['-', '-', '-', '-']
          },
          profitLoss: {
            columnDates: [
              'rkdp.FINYEAREND_DATE_LST3Y',
              'rkdp.FINYEAREND_DATE_LST2Y',
              'rkdp.FINYEAREND_DATE_LST1Y',
              'rkdp.FINYEAREND_DATE'
            ],
            salesRevenues: [
              'rkdp.TOTAL_REVENUE_LST3Y',
              'rkdp.TOTAL_REVENUE_LST2Y',
              'rkdp.TOTAL_REVENUE_LST1Y',
              'rkdp.TOTAL_REVENUE'
            ],
            result: [
              'rkdp.GROSS_PROFIT_LST3Y',
              'rkdp.GROSS_PROFIT_LST2Y',
              'rkdp.GROSS_PROFIT_LST1Y',
              'rkdp.GROSS_PROFIT'
            ],
            researchCosts: ['rkdp.RESEARCH_LST3Y', 'rkdp.RESEARCH_LST2Y', 'rkdp.RESEARCH_LST1Y', 'rkdp.RESEARCH'],
            extraordinaryResult: [
              'rkdp.EXTRAORDINARY_RESULT_LST3Y',
              'rkdp.EXTRAORDINARY_RESULT_LST2Y',
              'rkdp.EXTRAORDINARY_RESULT_LST1Y',
              'rkdp.EXTRAORDINARY_RESULT'
            ],
            taxes: ['rkdp.TAXES_LST3Y', 'rkdp.TAXES_LST2Y', 'rkdp.TAXES_LST1Y', 'rkdp.TAXES'],
            annualNetProfit: [
              'rkdp.ANNUAL_NET_PROFIT_LST3Y',
              'rkdp.ANNUAL_NET_PROFIT_LST2Y',
              'rkdp.ANNUAL_NET_PROFIT_LST1Y',
              'rkdp.ANNUAL_NET_PROFIT'
            ],
            ebit: ['rkdp.EBIT_LST3Y', 'rkdp.EBIT_LST2Y', 'rkdp.EBIT_LST1Y', 'rkdp.EBIT'],
            ebitda: ['rkdp.EBITDA_LST3Y', 'rkdp.EBITDA_LST2Y', 'rkdp.EBITDA_LST1Y', 'rkdp.EBITDA'],
            allValues: ['-', '-', '-', '-']
          },
          cashFlow: {
            columnDates: [
              'rkdp.FINYEAREND_DATE_LST3Y',
              'rkdp.FINYEAREND_DATE_LST2Y',
              'rkdp.FINYEAREND_DATE_LST1Y',
              'rkdp.FINYEAREND_DATE'
            ],
            cashFlowOperating: [
              'rkdp.CASH_FLOW_OPERATING_LST3Y',
              'rkdp.CASH_FLOW_OPERATING_LST2Y',
              'rkdp.CASH_FLOW_OPERATING_LST1Y',
              'rkdp.CASH_FLOW_OPERATING'
            ],
            cashFlowFinancing: [
              'rkdp.CASH_FLOW_FINANCING_LST3Y',
              'rkdp.CASH_FLOW_FINANCING_LST2Y',
              'rkdp.CASH_FLOW_FINANCING_LST1Y',
              'rkdp.CASH_FLOW_FINANCING'
            ],
            cashFlowInvesting: [
              'rkdp.CASH_FLOW_INVESTING_LST3Y',
              'rkdp.CASH_FLOW_INVESTING_LST2Y',
              'rkdp.CASH_FLOW_INVESTING_LST1Y',
              'rkdp.CASH_FLOW_INVESTING'
            ],
            allValues: ['-', '-', '-', '-']
          }
        },
        FIN: {
          balance: {
            columnDates: [
              'rkdp.FINYEAREND_DATE_LST3Y',
              'rkdp.FINYEAREND_DATE_LST2Y',
              'rkdp.FINYEAREND_DATE_LST1Y',
              'rkdp.FINYEAREND_DATE'
            ],
            accountingMethod: [
              'rkdp.ACCOUNTING_STD_LST3Y',
              'rkdp.ACCOUNTING_STD_LST2Y',
              'rkdp.ACCOUNTING_STD_LST1Y',
              'rkdp.ACCOUNTING_STD'
            ],
            assets: ['-', '-', '-', '-'],
            cashReserve: [
              'rkdp.CASH_AND_EQUIV_LST3Y',
              'rkdp.CASH_AND_EQUIV_LST2Y',
              'rkdp.CASH_AND_EQUIV_LST1Y',
              'rkdp.CASH_AND_EQUIV'
            ],
            intangibleAssets: [
              'rkdp.INTANGIBLE_ASSETS_LST3Y',
              'rkdp.INTANGIBLE_ASSETS_LST2Y',
              'rkdp.INTANGIBLE_ASSETS_LST1Y',
              'rkdp.INTANGIBLE_ASSETS'
            ],
            longTermInvestments: [
              'rkdp.BANK_LONG_INVESTMENTS_LST3Y',
              'rkdp.BANK_LONG_INVESTMENTS_LST2Y',
              'rkdp.BANK_LONG_INVESTMENTS_LST1Y',
              'rkdp.BANK_LONG_INVESTMENTS'
            ],
            receivables: [
              'rkdp.INS_RECEIVABLES_LST3Y',
              'rkdp.INS_RECEIVABLES_LST2Y',
              'rkdp.INS_RECEIVABLES_LST1Y',
              'rkdp.INS_RECEIVABLES'
            ],
            taxDeferrals: [
              'rkdp.INS_DEFERRED_TAX_ASSET_LST3Y',
              'rkdp.INS_DEFERRED_TAX_ASSET_LST2Y',
              'rkdp.INS_DEFERRED_TAX_ASSET_LST1Y',
              'rkdp.INS_DEFERRED_TAX_ASSET'
            ],
            fixedAccruals: ['rkdp.ACCRUALS_LST3Y', 'rkdp.ACCRUALS_LST2Y', 'rkdp.ACCRUALS_LST1Y', 'rkdp.ACCRUALS'],
            totalOtherAssets: [
              'rkdp.OTHER_ASSETS_LST3Y',
              'rkdp.OTHER_ASSETS_LST2Y',
              'rkdp.OTHER_ASSETS_LST1Y',
              'rkdp.OTHER_ASSETS'
            ],
            reinsuranceAssets: [
              'rkdp.INS_REINSURANCE_ASSETS_LST3Y',
              'rkdp.INS_REINSURANCE_ASSETS_LST2Y',
              'rkdp.INS_REINSURANCE_ASSETS_LST1Y',
              'rkdp.INS_REINSURANCE_ASSETS'
            ],
            otherAssets: [
              'rkdp.INS_OTHER_ASSETS_LST3Y',
              'rkdp.INS_OTHER_ASSETS_LST2Y',
              'rkdp.INS_OTHER_ASSETS_LST1Y',
              'rkdp.INS_OTHER_ASSETS'
            ],
            balanceSheetTotal: [
              'rkdp.TOTAL_ASSETS_LST3Y',
              'rkdp.TOTAL_ASSETS_LST2Y',
              'rkdp.TOTAL_ASSETS_LST1Y',
              'rkdp.TOTAL_ASSETS'
            ],
            liabilities: ['-', '-', '-', '-'],
            equityCapital: ['rkdp.EQUITY_LST3Y', 'rkdp.EQUITY_LST2Y', 'rkdp.EQUITY_LST1Y', 'rkdp.EQUITY'],
            liabilitiesFIN: [
              'rkdp.LIABILITIES_LST3Y',
              'rkdp.LIABILITIES_LST2Y',
              'rkdp.LIABILITIES_LST1Y',
              'rkdp.LIABILITIES'
            ],
            longTermLiabilities: [
              'rkdp.LONG_TERM_DEBT_LST3Y',
              'rkdp.LONG_TERM_DEBT_LST2Y',
              'rkdp.LONG_TERM_DEBT_LST1Y',
              'rkdp.LONG_TERM_DEBT'
            ],
            taxAccruals: [
              'rkdp.INS_DEFERRED_TAX_LST3Y',
              'rkdp.INS_DEFERRED_TAX_LST2Y',
              'rkdp.INS_DEFERRED_TAX_LST1Y',
              'rkdp.INS_DEFERRED_TAX'
            ],
            provisions: ['rkdp.PROVISION_LST3Y', 'rkdp.PROVISION_LST2Y', 'rkdp.PROVISION_LST1Y', 'rkdp.PROVISION'],
            minorityInterest: [
              'rkdp.MINORITY_INTEREST_LST3Y',
              'rkdp.MINORITY_INTEREST_LST2Y',
              'rkdp.MINORITY_INTEREST_LST1Y',
              'rkdp.MINORITY_INTEREST'
            ],
            otherLiabilities: [
              'rkdp.BANK_OTHER_LIABILITIES_LST3Y',
              'rkdp.BANK_OTHER_LIABILITIES_LST2Y',
              'rkdp.BANK_OTHER_LIABILITIES_LST1Y',
              'rkdp.BANK_OTHER_LIABILITIES'
            ],
            balanceSheetTotal2: [
              'rkdp.TOTAL_ASSETS_LST3Y',
              'rkdp.TOTAL_ASSETS_LST2Y',
              'rkdp.TOTAL_ASSETS_LST1Y',
              'rkdp.TOTAL_ASSETS'
            ],
            allValues: ['-', '-', '-', '-']
          },
          profitLoss: {
            columnDates: [
              'rkdp.FINYEAREND_DATE_LST3Y',
              'rkdp.FINYEAREND_DATE_LST2Y',
              'rkdp.FINYEAREND_DATE_LST1Y',
              'rkdp.FINYEAREND_DATE'
            ],
            independentExpenses: [
              'rkdp.TOTAL_REVENUE_LST3Y',
              'rkdp.TOTAL_REVENUE_LST2Y',
              'rkdp.TOTAL_REVENUE_LST1Y',
              'rkdp.TOTAL_REVENUE'
            ],
            totalExpenses: [
              'rkdp.TOTAL_EXPENSES_LST3Y',
              'rkdp.TOTAL_EXPENSES_LST2Y',
              'rkdp.TOTAL_EXPENSES_LST1Y',
              'rkdp.TOTAL_EXPENSES'
            ],
            ebit: ['rkdp.EBIT_LST3Y', 'rkdp.EBIT_LST2Y', 'rkdp.EBIT_LST1Y', 'rkdp.EBIT'],
            taxes: ['rkdp.TAXES_LST3Y', 'rkdp.TAXES_LST2Y', 'rkdp.TAXES_LST1Y', 'rkdp.TAXES'],
            annualNetProfit: [
              'rkdp.ANNUAL_NET_PROFIT_LST3Y',
              'rkdp.ANNUAL_NET_PROFIT_LST2Y',
              'rkdp.ANNUAL_NET_PROFIT_LST1Y',
              'rkdp.ANNUAL_NET_PROFIT'
            ],
            allValues: ['-', '-', '-', '-']
          },
          cashFlow: {
            columnDates: [
              'rkdp.FINYEAREND_DATE_LST3Y',
              'rkdp.FINYEAREND_DATE_LST2Y',
              'rkdp.FINYEAREND_DATE_LST1Y',
              'rkdp.FINYEAREND_DATE'
            ],
            cashFlowOperating: [
              'rkdp.CASH_FLOW_OPERATING_LST3Y',
              'rkdp.CASH_FLOW_OPERATING_LST2Y',
              'rkdp.CASH_FLOW_OPERATING_LST1Y',
              'rkdp.CASH_FLOW_OPERATING'
            ],
            cashFlowFinancing: [
              'rkdp.CASH_FLOW_FINANCING_LST3Y',
              'rkdp.CASH_FLOW_FINANCING_LST2Y',
              'rkdp.CASH_FLOW_FINANCING_LST1Y',
              'rkdp.CASH_FLOW_FINANCING'
            ],
            cashFlowInvesting: [
              'rkdp.CASH_FLOW_INVESTING_LST3Y',
              'rkdp.CASH_FLOW_INVESTING_LST2Y',
              'rkdp.CASH_FLOW_INVESTING_LST1Y',
              'rkdp.CASH_FLOW_INVESTING'
            ],
            allValues: ['-', '-', '-', '-']
          }
        },
        UTL: {
          balance: {
            columnDates: [
              'rkdp.FINYEAREND_DATE_LST3Y',
              'rkdp.FINYEAREND_DATE_LST2Y',
              'rkdp.FINYEAREND_DATE_LST1Y',
              'rkdp.FINYEAREND_DATE'
            ],
            accountingMethod: [
              'rkdp.ACCOUNTING_STD_LST3Y',
              'rkdp.ACCOUNTING_STD_LST2Y',
              'rkdp.ACCOUNTING_STD_LST1Y',
              'rkdp.ACCOUNTING_STD'
            ],
            assets: ['-', '-', '-', '-'],
            shortTermAssets: [
              'rkdp.WORKING_ASSETS_LST3Y',
              'rkdp.WORKING_ASSETS_LST2Y',
              'rkdp.WORKING_ASSETS_LST1Y',
              'rkdp.WORKING_ASSETS'
            ],
            liquidFunds: [
              'rkdp.CASH_AND_EQUIV_LST3Y',
              'rkdp.CASH_AND_EQUIV_LST2Y',
              'rkdp.CASH_AND_EQUIV_LST1Y',
              'rkdp.CASH_AND_EQUIV'
            ],
            receivables: [
              'rkdp.RECEIVABLES_LST3Y',
              'rkdp.RECEIVABLES_LST2Y',
              'rkdp.RECEIVABLES_LST1Y',
              'rkdp.RECEIVABLES'
            ],
            otherShortTimeAssets: [
              'rkdp.OTHER_CURRENT_ASSETS_LST3Y',
              'rkdp.OTHER_CURRENT_ASSETS_LST2Y',
              'rkdp.OTHER_CURRENT_ASSETS_LST1Y',
              'rkdp.OTHER_CURRENT_ASSETS'
            ],
            longTermValues: [
              'rkdp.UTL_DISCONTINUED_ASSETS_LST3Y',
              'rkdp.UTL_DISCONTINUED_ASSETS_LST2Y',
              'rkdp.UTL_DISCONTINUED_ASSETS_LST1Y',
              'rkdp.UTL_DISCONTINUED_ASSETS'
            ],
            longTermAssets: [
              'rkdp.FIXED_ASSETS_LST3Y',
              'rkdp.FIXED_ASSETS_LST2Y',
              'rkdp.FIXED_ASSETS_LST1Y',
              'rkdp.FIXED_ASSETS'
            ],
            longTermAssetsUTL: [
              'rkdp.UTL_PLANT_TOT_LST3Y',
              'rkdp.UTL_PLANT_TOT_LST2Y',
              'rkdp.UTL_PLANT_TOT_LST1Y',
              'rkdp.UTL_PLANT_TOT'
            ],
            intangibleAssets: [
              'rkdp.INTANGIBLE_ASSETS_LST3Y',
              'rkdp.INTANGIBLE_ASSETS_LST2Y',
              'rkdp.INTANGIBLE_ASSETS_LST1Y',
              'rkdp.INTANGIBLE_ASSETS',
              'rkdp.GOODWILL_LST3Y',
              'rkdp.GOODWILL_LST2Y',
              'rkdp.GOODWILL_LST1Y',
              'rkdp.GOODWILL'
            ],
            longTermInvestments: [
              'rkdp.BANK_LONG_INVESTMENTS_LST3Y',
              'rkdp.BANK_LONG_INVESTMENTS_LST2Y',
              'rkdp.BANK_LONG_INVESTMENTS_LST1Y',
              'rkdp.BANK_LONG_INVESTMENTS'
            ],
            taxAccruals: [
              'rkdp.UTL_DEFERRED_TAX_ASSET_LST3Y',
              'rkdp.UTL_DEFERRED_TAX_ASSET_LST2Y',
              'rkdp.UTL_DEFERRED_TAX_ASSET_LST1Y',
              'rkdp.UTL_DEFERRED_TAX_ASSET'
            ],
            balanceSheetTotal: [
              'rkdp.TOTAL_ASSETS_LST3Y',
              'rkdp.TOTAL_ASSETS_LST2Y',
              'rkdp.TOTAL_ASSETS_LST1Y',
              'rkdp.TOTAL_ASSETS'
            ],
            liabilities: [
              'rkdp.FINYEAREND_DATE_LST3Y',
              'rkdp.FINYEAREND_DATE_LST2Y',
              'rkdp.FINYEAREND_DATE_LST1Y',
              'rkdp.FINYEAREND_DATE'
            ],
            equityCapital: ['rkdp.EQUITY_LST3Y', 'rkdp.EQUITY_LST2Y', 'rkdp.EQUITY_LST1Y', 'rkdp.EQUITY'],
            capitalStock: [
              'rkdp.NOMINAL_CAPITAL_LST3Y',
              'rkdp.NOMINAL_CAPITAL_LST2Y',
              'rkdp.NOMINAL_CAPITAL_LST1Y',
              'rkdp.NOMINAL_CAPITAL'
            ],
            retainedEarnings: [
              'rkdp.RETAINED_EARNINGS_LST3Y',
              'rkdp.RETAINED_EARNINGS_LST2Y',
              'rkdp.RETAINED_EARNINGS_LST1Y',
              'rkdp.RETAINED_EARNINGS'
            ],
            capitalReserves: [
              'rkdp.CAPITAL_RESERVES_LST3Y',
              'rkdp.CAPITAL_RESERVES_LST2Y',
              'rkdp.CAPITAL_RESERVES_LST1Y',
              'rkdp.CAPITAL_RESERVES'
            ],
            treasuryStock: [
              'rkdp.TREASURY_STOCK_LST3Y',
              'rkdp.TREASURY_STOCK_LST2Y',
              'rkdp.TREASURY_STOCK_LST1Y',
              'rkdp.TREASURY_STOCK'
            ],
            otherLiabilitiesUTL: [
              'rkdp.UTL_OTHER_LIABILITIES_LST3Y',
              'rkdp.UTL_OTHER_LIABILITIES_LST2Y',
              'rkdp.UTL_OTHER_LIABILITIES_LST1Y',
              'rkdp.UTL_OTHER_LIABILITIES'
            ],
            longTermLiabilities: [
              'rkdp.LONG_TERM_DEBT_LST3Y',
              'rkdp.LONG_TERM_DEBT_LST2Y',
              'rkdp.LONG_TERM_DEBT_LST1Y',
              'rkdp.LONG_TERM_DEBT'
            ],
            taxDeferrals: [
              'rkdp.DEFERRED_INCOME_LST3Y',
              'rkdp.DEFERRED_INCOME_LST2Y',
              'rkdp.DEFERRED_INCOME_LST1Y',
              'rkdp.DEFERRED_INCOME'
            ],
            provisions: [
              'rkdp.PENSION_BENEFITS_LST3Y',
              'rkdp.PENSION_BENEFITS_LST2Y',
              'rkdp.PENSION_BENEFITS_LST1Y',
              'rkdp.PENSION_BENEFITS',
              'rkdp.PROVISION_LST3Y',
              'rkdp.PROVISION_LST2Y',
              'rkdp.PROVISION_LST1Y',
              'rkdp.PROVISION'
            ],
            tradePayables: [
              'rkdp.TRADE_PAYABLES_LST3Y',
              'rkdp.TRADE_PAYABLES_LST2Y',
              'rkdp.TRADE_PAYABLES_LST1Y',
              'rkdp.TRADE_PAYABLES'
            ],
            otherShortTermLiabilities: [
              'rkdp.OTHER_CURRENT_LIABS_LST3Y',
              'rkdp.OTHER_CURRENT_LIABS_LST2Y',
              'rkdp.OTHER_CURRENT_LIABS_LST1Y',
              'rkdp.OTHER_CURRENT_LIABS'
            ],
            minorityInterest: [
              'rkdp.MINORITY_INTEREST_LST3Y',
              'rkdp.MINORITY_INTEREST_LST2Y',
              'rkdp.MINORITY_INTEREST_LST1Y',
              'rkdp.MINORITY_INTEREST'
            ],
            balanceSheetTotal2: [
              'rkdp.TOTAL_ASSETS_LST3Y',
              'rkdp.TOTAL_ASSETS_LST2Y',
              'rkdp.TOTAL_ASSETS_LST1Y',
              'rkdp.TOTAL_ASSETS'
            ],
            allValues: ['-', '-', '-', '-']
          },
          profitLoss: {
            columnDates: [
              'rkdp.FINYEAREND_DATE_LST3Y',
              'rkdp.FINYEAREND_DATE_LST2Y',
              'rkdp.FINYEAREND_DATE_LST1Y',
              'rkdp.FINYEAREND_DATE'
            ],
            independentExpenses: [
              'rkdp.TOTAL_REVENUE_LST3Y',
              'rkdp.TOTAL_REVENUE_LST2Y',
              'rkdp.TOTAL_REVENUE_LST1Y',
              'rkdp.TOTAL_REVENUE'
            ],
            totalExpenses: [
              'rkdp.TOTAL_EXPENSES_LST3Y',
              'rkdp.TOTAL_EXPENSES_LST2Y',
              'rkdp.TOTAL_EXPENSES_LST1Y',
              'rkdp.TOTAL_EXPENSES'
            ],
            ebit: ['rkdp.EBIT_LST3Y', 'rkdp.EBIT_LST2Y', 'rkdp.EBIT_LST1Y', 'rkdp.EBIT'],
            taxes: ['rkdp.TAXES_LST3Y', 'rkdp.TAXES_LST2Y', 'rkdp.TAXES_LST1Y', 'rkdp.TAXES'],
            annualNetProfit: [
              'rkdp.ANNUAL_NET_PROFIT_LST3Y',
              'rkdp.ANNUAL_NET_PROFIT_LST2Y',
              'rkdp.ANNUAL_NET_PROFIT_LST1Y',
              'rkdp.ANNUAL_NET_PROFIT'
            ],
            ebitda: ['rkdp.EBITDA_LST3Y', 'rkdp.EBITDA_LST2Y', 'rkdp.EBITDA_LST1Y', 'rkdp.EBITDA'],
            allValues: ['-', '-', '-', '-']
          },
          cashFlow: {
            columnDates: [
              'rkdp.FINYEAREND_DATE_LST3Y',
              'rkdp.FINYEAREND_DATE_LST2Y',
              'rkdp.FINYEAREND_DATE_LST1Y',
              'rkdp.FINYEAREND_DATE'
            ],
            cashFlowOperating: [
              'rkdp.CASH_FLOW_OPERATING_LST3Y',
              'rkdp.CASH_FLOW_OPERATING_LST2Y',
              'rkdp.CASH_FLOW_OPERATING_LST1Y',
              'rkdp.CASH_FLOW_OPERATING'
            ],
            cashFlowFinancing: [
              'rkdp.CASH_FLOW_FINANCING_LST3Y',
              'rkdp.CASH_FLOW_FINANCING_LST2Y',
              'rkdp.CASH_FLOW_FINANCING_LST1Y',
              'rkdp.CASH_FLOW_FINANCING'
            ],
            cashFlowInvesting: [
              'rkdp.CASH_FLOW_INVESTING_LST3Y',
              'rkdp.CASH_FLOW_INVESTING_LST2Y',
              'rkdp.CASH_FLOW_INVESTING_LST1Y',
              'rkdp.CASH_FLOW_INVESTING'
            ],
            allValues: ['-', '-', '-', '-']
          }
        }
      },
      tableFields: [
        {
          key: 'title',
          label: '',
          headerTitle: 'title',
          isRowHeader: true,
          thClass: 'bg-dark',
          tdClass: (value, key, item) => this.getTdClass(item)
        },
        {
          key: '3y',
          label: '',
          class: 'text-sm-right',
          thClass: 'bg-dark',
          tdClass: (value, key, item) => this.getTdClass(item),
          formatter: this.itemFormatter
        },
        {
          key: '2y',
          label: '',
          class: 'text-sm-right',
          thClass: 'bg-dark',
          tdClass: (value, key, item) => this.getTdClass(item),
          formatter: this.itemFormatter
        },
        {
          key: '1y',
          label: '',
          class: 'text-sm-right',
          thClass: 'bg-dark',
          tdClass: (value, key, item) => this.getTdClass(item),
          formatter: this.itemFormatter
        },
        {
          key: 'year_end',
          label: '',
          headerTitle: '',
          class: 'text-sm-right',
          thClass: 'bg-dark',
          tdClass: (value, key, item) => this.getTdClass(item),
          formatter: this.itemFormatter
        }
      ],
      tableColumns: ['title', '3y', '2y', '1y', 'year_end'],
      tableDates: {},
      tableFooter: {},
      balanceItems: [],
      profitLossItems: [],
      cashFlowItems: [],
      i18nNamespace: 'white-label.widgets.financialStatements'
    };
  },
  watch: {
    ric() {
      this.fetch();
    }
  },
  methods: {
    getItems(table) {
      return this[`${table}Items`];
    },
    getTableFields(table) {
      this.tableFields.find(o => o.key === '3y').headerTitle = this.tableDates[table]['3y'];
      this.tableFields.find(o => o.key === '2y').headerTitle = this.tableDates[table]['2y'];
      this.tableFields.find(o => o.key === '1y').headerTitle = this.tableDates[table]['1y'];
      this.tableFields.find(o => o.key === 'year_end').headerTitle = this.tableDates[table]['year_end'];
      return this.tableFields;
    },
    itemFormatter(val, key, item) {
      if (item.title === 'columnDates') {
        return this.$f(val, 'date.localed');
      } else if (item.title === 'assets' || item.title === 'liabilities' || item.title === 'allValues') {
        return '';
      } else {
        return val;
      }
    },
    getTdClass(row) {
      const classes = [];
      const subrows = [
        'depositsFinancialInstitutions',
        'bonds',
        'otherFinancialAssets',
        'reinsuranceAssets',
        'otherAssets',
        'property',
        'investments',
        'accrualsAndDeferrals',
        'shareCapital',
        'reserves',
        'deposits',
        'otherShortTermLiabilities',
        'longTermLiabilities',
        'otherLiabilities',
        'taxAccruals',
        'accruedExpenses',
        'minorityInterest',
        'provisions',
        'liquidFunds',
        'receivables',
        'otherShortTimeAssets',
        'longTermValues',
        'longTermAssetsUTL',
        'intangibleAssets',
        'longTermInvestments',
        'capitalStock',
        'retainedEarnings',
        'capitalReserves',
        'treasuryStock',
        'taxDeferrals',
        'tradePayables',
        'cashAssets',
        'otherCurrentAssets',
        'accruals',
        'fixedAccruals',
        'shortTermLiabilities'
      ];
      if (subrows.includes(row.title)) {
        classes.push('pl-4');
      } else if (row.title === 'balanceSheetTotal' || row.title === 'balanceSheetTotal2' || row.title === 'ebitda') {
        classes.push('font-weight-bold total-value');
      } else if (row.title === 'columnDates' || row.title === 'assets' || row.title === 'liabilities') {
        classes.push('bg-dark');
      }
      return classes;
    },
    onHeaderClicked() {
      return false;
    },
    fetchBalanceType() {
      return API.Rest.get(`/quote/info?rics=${this.ric}&fids=q.RIC,rkdp.COMP_COA_CD,rkdp.REPORT_CURR_FINANCIALS`);
    },
    fetchFinancialStatementsData() {
      const fids = this.generateFids(this.balanceType).toString();
      return API.Rest.get(`/quote/info?rics=${this.ric}&fids=q.RIC,${fids}`);
    },
    fetch() {
      this.isLoading = true;
      this.isNoData = false;
      this.hasError = false;

      this.balanceItems = this.profitLossItems = this.cashFlowItems = [];

      this.fetchBalanceType()
        .then(response => {
          if (response.data.data.length === 0) {
            this.isNoData = true;
            throw new Error('NO_FINANCIAL_STATEMENTS_RECEIVED');
          }

          const balanceTypeData = response.data.data;

          this.currency = balanceTypeData[0]['rkdp.REPORT_CURR_FINANCIALS'];
          this.balanceType = balanceTypeData[0]['rkdp.COMP_COA_CD'];
        })
        .then(this.fetchFinancialStatementsData)
        .then(res => {
          this.financialStatementsData = res.data.data[0];
          this.balanceItems = this.createTableItems(this.balanceType, 'balance');
          this.tableDates.balance = this.balanceItems.shift();
          this.tableFooter.balance = this.balanceItems.pop();
          this.profitLossItems = this.createTableItems(this.balanceType, 'profitLoss');
          this.tableDates.profitLoss = this.profitLossItems.shift();
          this.tableFooter.profitLoss = this.profitLossItems.pop();
          this.cashFlowItems = this.createTableItems(this.balanceType, 'cashFlow');
          this.tableDates.cashFlow = this.cashFlowItems.shift();
          this.tableFooter.cashFlow = this.cashFlowItems.pop();

          this.isLoading = false;
        })
        .catch(e => {
          this.isLoading = false;
          this.hasError = true;
          console.log(e);
        });
    },
    generateFids(balanceType) {
      let result = [];
      Object.keys(this.tables[balanceType]).forEach(table => {
        Object.keys(this.tables[balanceType][table]).forEach(row => {
          if (this.tables[balanceType][table][row][0] !== '-') {
            result = result.concat(this.tables[balanceType][table][row]);
          }
        });
      });
      return result;
    },
    createTableItems(balanceType, table) {
      const result = [];
      Object.keys(this.tables[balanceType][table]).forEach(row => {
        const tableRow = { title: row };
        Object.keys(this.tables[balanceType][table][row]).forEach((column, i) => {
          let val = this.financialStatementsData[this.tables[balanceType][table][row][column]];

          // Special calculated FIDs for UTL
          if (balanceType === 'UTL') {
            if (row === 'provisions') {
              const fid = this.tables[balanceType][table][row][column];
              const yearPostFix = fid.split('_')[2] !== undefined ? `_${fid.split('_')[2]}` : '';
              let provisionFid = `rkdp.PROVISION${yearPostFix}`;

              val = +this.financialStatementsData[fid] + +this.financialStatementsData[provisionFid];
            }

            if (row === 'otherLiabilitiesUTL') {
              const fid = this.tables[balanceType][table][row][column];
              const yearPostFix = fid.split('_')[3] !== undefined ? `_${fid.split('_')[3]}` : '';
              let totalAssetsFid = `rkdp.TOTAL_ASSETS${yearPostFix}`;
              let minorityInterestFid = `rkdp.MINORITY_INTEREST${yearPostFix}`;
              let equityFid = `rkdp.EQUITY${yearPostFix}`;

              val =
                +this.financialStatementsData[totalAssetsFid] -
                +this.financialStatementsData[minorityInterestFid] -
                +this.financialStatementsData[equityFid];
            }

            if (row === 'intangibleAssets') {
              const fid = this.tables[balanceType][table][row][column];
              const yearPostFix = fid.split('_')[2] !== undefined ? `_${fid.split('_')[2]}` : '';
              let goodwillFid = `rkdp.GOODWILL${yearPostFix}`;
              val = +this.financialStatementsData[fid] + +this.financialStatementsData[goodwillFid];
            }
          }

          if (
            row !== 'columnDates' &&
            row !== 'accountingMethod' &&
            row !== 'liabilities' &&
            val !== undefined &&
            val !== '-' &&
            val !== ''
          ) {
            tableRow[this.tableColumns[i + 1]] = val;
          } else {
            tableRow.noFormatting = true;
            tableRow[this.tableColumns[i + 1]] = val !== undefined ? val : '-';
          }
        });
        result.push(tableRow);
      });
      return result;
    },
    suppressTab(event) {
      event.preventDefault();
    }
  },
  mounted() {
    this.fetch();
  }
};
</script>
